import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { deckService } from '../services/deckService';
import copyService from '../services/copyService';
import { extractIdFromSlug } from '../utils/slugUtils';
import { t } from '../utils/translations';
import { 
  LightBulbIcon, 
  EyeIcon, 
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import ThemeToggle from '../components/ThemeToggle';
import OAuthButtons from '../components/auth/OAuthButtons';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle OAuth errors
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'oauth_error') {
      toast.error('Falha na autenticação OAuth. Você precisa criar uma conta primeiro ou já ter uma conta vinculada ao Google.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login({ email, password });
      
      // Check if there's a redirect URL
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        // If redirecting to a study page, we need to copy the deck first
        if (redirectTo.startsWith('/study/')) {
          const deckSlug = redirectTo.split('/study/')[1];
          const deckId = extractIdFromSlug(deckSlug);
          try {
            // Copy the deck to user's collection
            const copiedDeck = await deckService.copyDeck(deckId);
            
            toast.success(`Deck "${copiedDeck.name}" copiado com sucesso!`);
            
            // Redirect to study the copied deck
            navigate(`/study/${copiedDeck._id}`);
          } catch (error: any) {
            console.error('Error copying deck:', error);
            
            // Handle specific error cases
            if (error.response?.data?.code === 'DECK_LIMIT_REACHED') {
              toast.error('Limite de decks atingido. Faça upgrade para Premium.');
            } else if (error.response?.data?.code === 'COPY_CARD_LIMIT_REACHED') {
              toast.error('Deck tem muitos cards para usuários gratuitos. Faça upgrade para Premium.');
            } else if (error.response?.status === 404) {
              toast.error('Deck não encontrado ou não está mais disponível.');
            } else {
              toast.error('Erro ao copiar deck. Tente novamente.');
            }
            
            // Fallback to home page
            navigate('/home');
          }
        } else if (redirectTo.startsWith('/quizzes/management/')) {
          // Handle quiz copying
          const quizSlug = redirectTo.split('/quizzes/management/')[1];
          try {
            const id = extractIdFromSlug(quizSlug);
            const result = await copyService.copyQuiz(id);
            toast.success(result.message);
            navigate('/quizzes');
          } catch (error: any) {
            console.error('Error copying quiz:', error);
            toast.error(error.response?.data?.message || 'Erro ao copiar quiz');
            navigate('/home');
          }
        } else if (redirectTo.includes('/matches/')) {
          // Handle match copying (URLs like /matches/{slug}/play or /matches/{slug})
          const matchSlug = redirectTo.split('/matches/')[1]?.split('/')[0];
          if (matchSlug) {
            try {
              const id = extractIdFromSlug(matchSlug);
              const result = await copyService.copyMatch(id);
              toast.success(result.message);
              navigate('/matches');
            } catch (error: any) {
              console.error('Error copying match:', error);
              toast.error(error.response?.data?.message || 'Erro ao copiar jogo');
              navigate('/home');
            }
          } else {
            navigate(redirectTo);
          }
        } else {
          navigate(redirectTo);
        }
      } else {
        navigate('/home');
      }
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-32 w-96 h-96 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-32 -left-40 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
      </div>
      
      {/* Theme Toggle */}
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      
      <div className="relative w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 rounded-3xl flex items-center justify-center mb-6 shadow-xl transform hover:scale-105 transition-transform duration-300">
            <img 
              src="/favicon.png" 
              alt="Upmindy Logo" 
              className="h-16 w-16 rounded-2xl"
            />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent mb-3">
            Entrar
          </h1>
        </div>

        {/* OAuth Buttons */}
        <OAuthButtons type="login" disabled={loading} className="mb-6" />

        {/* Main Form Card */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20 dark:border-gray-700/50">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="relative group">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.emailAddress')}
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full pl-4 pr-4 py-4 bg-gray-50/50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('auth.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="relative group">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="w-full pl-4 pr-12 py-4 bg-gray-50/50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('auth.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none transition-all duration-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t('auth.signingIn')}</span>
                </div>
              ) : (
                <>
                  <span>{t('auth.signIn')}</span>
                  <LightBulbIcon className="h-5 w-5" />
                </>
              )}
            </button>

            {/* Forgot Password Link */}
            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>
          </form>
        </div>

        {/* Register Link */}
        <div className="text-center mt-6">
          <p className="text-gray-600 dark:text-gray-400">
            {t('auth.dontHaveAccount')}{' '}
            <Link 
              to={`/register${searchParams.get('redirect') ? `?redirect=${encodeURIComponent(searchParams.get('redirect')!)}` : ''}`}
              className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              {t('auth.signUp')}
            </Link>
          </p>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-500 dark:text-gray-400">
            <Link 
              to="/terms-of-service" 
              className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Termos de Uso
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link 
              to="/privacy-policy" 
              className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Política de Privacidade
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
